// Fixed header strip identifying the run this snapshot describes. The
// data-as-of date is the boldest element — every number below is
// point-in-time as of that date. The bordered chip names the comparison
// baseline; it is absent when the run has no predecessor.
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { RunStatus } from "../../services/appsync/types";
import { SnapshotPeriod, SnapshotRunInfo } from "./types";
import { useSnapshotTokens } from "./primitives";

/** Two-option segmented control. Scopes FLOW figures only — stocks and
 *  rates are point-in-time and identical in both states. */
const PeriodToggle = ({
  period,
  weekLabel,
  monthLabel,
  monthWeeks,
  onChange,
}: {
  period: SnapshotPeriod;
  weekLabel: string;
  monthLabel: string;
  monthWeeks: number;
  onChange: (next: SnapshotPeriod) => void;
}) => {
  const t = useSnapshotTokens();
  const options: { id: SnapshotPeriod; label: string; title?: string }[] = [
    { id: "week", label: `Week of ${weekLabel}` },
    {
      id: "month",
      label: `${monthLabel} to date`,
      // A month-to-date total is short by design early in the month. Say how
      // much of the month it covers, so a small figure reads as partial
      // rather than broken.
      title:
        monthWeeks > 0
          ? `${monthLabel} to date — the ${monthWeeks} complete ${
              monthWeeks === 1 ? "week" : "weeks"
            } of the month so far. The in-progress week is excluded: sales_order_master fills it with forecast.`
          : `${monthLabel} has no complete week yet, so month figures are unavailable. The in-progress week is excluded: sales_order_master fills it with forecast.`,
    },
  ];
  return (
    <div
      role="group"
      aria-label="Flow period"
      title="Changes outflow figures only — stock figures stay point-in-time"
      style={{
        display: "inline-flex",
        border: `1px solid ${t.line}`,
        borderRadius: 7,
        overflow: "hidden",
        background: t.surface3,
      }}
    >
      {options.map((o) => {
        const active = o.id === period;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={active}
            title={o.title}
            onClick={() => onChange(o.id)}
            style={{
              appearance: "none",
              border: "none",
              cursor: active ? "default" : "pointer",
              padding: "4px 11px",
              fontSize: 11.5,
              fontWeight: active ? 700 : 500,
              fontFamily: "inherit",
              color: active ? t.blueDeep : t.ink3,
              background: active ? t.surface : "transparent",
              boxShadow: active ? `inset 0 0 0 1px ${t.blueSoft}` : "none",
              whiteSpace: "nowrap",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

const STATUS_CHIP: Partial<
  Record<
    RunStatus,
    { label: string; color: "info" | "success" | "error" | "warning" }
  >
> = {
  RUN_IN_PROGRESS: { label: "In Progress", color: "info" },
  RUN_COMPLETED: { label: "Completed", color: "success" },
  RUN_FAILED: { label: "Failed", color: "error" },
  CANCEL_IN_PROGRESS: { label: "Cancelling", color: "warning" },
  CANCEL_COMPLETED: { label: "Cancelled", color: "info" },
  CANCEL_FAILED: { label: "Cancel Failed", color: "error" },
};

export const RunBar = ({
  run,
  period,
  weekLabel,
  monthLabel,
  monthWeeks,
  onPeriodChange,
  loading = false,
}: {
  run: SnapshotRunInfo;
  period: SnapshotPeriod;
  weekLabel: string;
  monthLabel: string;
  monthWeeks: number;
  onPeriodChange: (next: SnapshotPeriod) => void;
  /** Flow data refreshing; shown inline so the body can stay rendered. */
  loading?: boolean;
}) => {
  const t = useSnapshotTokens();
  const status = STATUS_CHIP[run.status];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "13px 22px",
        borderBottom: `1px solid ${t.line}`,
        background: t.surface,
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
        <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.3 }}>
          Run Report Card
        </span>
        {run.displayId && (
          <span style={{ fontSize: 13, color: t.ink3 }}>
            Run {run.displayId}
          </span>
        )}
      </div>
      <span style={{ width: 1, height: 18, background: t.line }} />
      {run.name && (
        <span style={{ fontSize: 12.5, color: t.ink2, fontWeight: 500 }}>
          {run.name}
        </span>
      )}
      {status && (
        <Chip
          size="small"
          variant="outlined"
          label={status.label}
          color={status.color}
        />
      )}
      <PeriodToggle
        period={period}
        weekLabel={weekLabel}
        monthLabel={monthLabel}
        monthWeeks={monthWeeks}
        onChange={onPeriodChange}
      />
      {loading && (
        <CircularProgress size={13} thickness={5} sx={{ color: "atomic.muted" }} />
      )}
      <div style={{ flex: 1 }} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          fontSize: 11.5,
          color: t.ink3,
        }}
      >
        <span>
          <span style={{ color: t.ink4 }}>Data as of </span>
          <b style={{ color: t.ink2, fontWeight: 700 }}>{run.asOf}</b>
        </span>
        <span>
          <span style={{ color: t.ink4 }}>Ran </span>
          {run.finishedAt}
        </span>
        <span>
          <span style={{ color: t.ink4 }}>Settings </span>
          {run.settingsName}
        </span>
        {run.prevDisplayId && (
          <span
            title="All delta chips on this page compare against this run"
            style={{
              padding: "3px 9px",
              border: `1px solid ${t.line}`,
              borderRadius: 6,
              background: t.surface3,
            }}
          >
            vs run {run.prevDisplayId}
          </span>
        )}
      </div>
    </div>
  );
};
